//fetches and prepares table data.
import { useEffect, useState } from 'react';
import { supabase } from '../../context/AuthContext';
import { convertToMelbourneDate } from '../../utils/dateUtils';

const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getMelbourneDate(date) {
  const melDate = convertToMelbourneDate(date);
  const [year, month, day] = melDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function getOverallRanges() {
  const now = new Date();
  const melNow = getMelbourneDate(now);
  const day = melNow.getDay();
  const weekDiff = (day === 0 ? -6 : 1) - day;
  const weekStart = new Date(melNow);
  weekStart.setDate(melNow.getDate() + weekDiff);

  return [
    {
      label: 'Today',
      start: new Date(melNow.getFullYear(), melNow.getMonth(), melNow.getDate()).toISOString(),
      end: new Date(melNow.getFullYear(), melNow.getMonth(), melNow.getDate() + 1).toISOString(),
    },
    {
      label: 'This Week',
      start: weekStart.toISOString(),
      end: new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7).toISOString(),
    },
    {
      label: 'This Month',
      start: new Date(melNow.getFullYear(), melNow.getMonth(), 1).toISOString(),
      end: new Date(melNow.getFullYear(), melNow.getMonth() + 1, 1).toISOString(),
    },
    {
      label: 'This Year',
      start: new Date(melNow.getFullYear(), 0, 1).toISOString(),
      end: new Date(melNow.getFullYear() + 1, 0, 1).toISOString(),
    },
  ];
}

function getDailyRanges() {
  return Array.from({ length: 7 }, (_, daysAgo) => {
    const now = new Date();
    now.setDate(now.getDate() - daysAgo);
    const melDate = convertToMelbourneDate(now);
    const [year, month, day] = melDate.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    const end = new Date(year, month - 1, day + 1);

    return {
      label:
        daysAgo === 0
          ? 'Today'
          : daysAgo === 1
            ? 'Yesterday'
            : daysAgo === 2
              ? 'Day Before'
              : `${daysAgo} Days Ago`,
      secondaryLabel: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`,
      start: start.toISOString(),
      end: end.toISOString(),
    };
  });
}

function getWeeklyRanges(numWeeks = 5) {
  const now = new Date();
  const melNow = getMelbourneDate(now);
  const dayOfWeek = melNow.getDay();
  const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
  const thisMonday = new Date(melNow);
  thisMonday.setDate(melNow.getDate() + diff);
  thisMonday.setHours(0, 0, 0, 0);

  return Array.from({ length: numWeeks }, (_, index) => {
    const start = new Date(thisMonday);
    start.setDate(thisMonday.getDate() - index * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    const endDisplay = new Date(end);
    endDisplay.setDate(end.getDate() - 1);

    return {
      label:
        index === 0
          ? 'This Week'
          : index === 1
            ? 'Last Week'
            : index === 2
              ? 'Week Before'
              : `Week (${index} Weeks Ago)`,
      secondaryLabel: `${String(start.getDate()).padStart(2, '0')}/${String(start.getMonth() + 1).padStart(2, '0')}/${start.getFullYear()}–${String(endDisplay.getDate()).padStart(2, '0')}/${String(endDisplay.getMonth() + 1).padStart(2, '0')}/${endDisplay.getFullYear()}`,
      start: start.toISOString(),
      end: end.toISOString(),
    };
  });
}

function getMonthlyRanges() {
  const startYear = 2025;
  const startMonth = 1;
  const now = new Date();
  const melDate = convertToMelbourneDate(now);
  const [year, month] = melDate.split('-').map(Number);
  const endYear = year;
  const endMonth = month - 1;
  const months = [];
  let currentYear = startYear;
  let currentMonth = startMonth;

  while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
    const start = new Date(currentYear, currentMonth, 1);
    const end = new Date(currentYear, currentMonth + 1, 1);
    months.push({
      label: `${MONTHS[currentMonth]} ${currentYear}`,
      start: start.toISOString(),
      end: end.toISOString(),
    });
    currentMonth += 1;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear += 1;
    }
  }

  return months.reverse();
}

const TABLE_CONFIG = [
  {
    title: 'Overall Analytics',
    primaryHeader: 'Timeframe',
    ranges: getOverallRanges,
  },
  {
    title: 'Daily Analytics',
    primaryHeader: 'Day',
    secondaryHeader: 'Date',
    ranges: getDailyRanges,
  },
  {
    title: 'Weekly Analytics',
    primaryHeader: 'Week',
    secondaryHeader: 'Date Range',
    ranges: getWeeklyRanges,
  },
  {
    title: 'Monthly Analytics',
    primaryHeader: 'Month',
    ranges: getMonthlyRanges,
  },
];

function buildEmptyStateTotals() {
  return STATES.reduce((accumulator, state) => {
    accumulator[state] = 0;
    return accumulator;
  }, {});
}

export function useAnalyticsSections(mode) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const fetchSections = async () => {
      setLoading(true);
      const nextSections = [];

      for (const config of TABLE_CONFIG) {
        const ranges = config.ranges();
        const rows = [];

        for (const range of ranges) {
          let query = supabase
            .from('admin_submissions_view')
            .select(mode === 'revenue' ? 'state, status, transaction_price, created_at' : 'state, status, created_at');

          query = query
            .eq('is_archived', false)
            .gte('created_at', range.start)
            .lt('created_at', range.end);

          const { data, error } = await query;
          const stateTotals = buildEmptyStateTotals();
          let paid = 0;
          let unpaid = 0;
          let total = 0;

          if (!error && data) {
            data.forEach((row) => {
              const value = mode === 'revenue' ? Number(row.transaction_price) || 0 : 1;

              if (row.state && STATES.includes(row.state)) {
                stateTotals[row.state] += value;
              }

              if (row.status === 'Paid') {
                paid += value;
              } else {
                unpaid += value;
              }

              total += value;
            });
          }

          rows.push({
            label: range.label,
            secondaryLabel: range.secondaryLabel,
            ...stateTotals,
            total,
            paid,
            unpaid,
          });
        }

        nextSections.push({ ...config, rows });
      }

      if (isActive) {
        setSections(nextSections);
        setLoading(false);
      }
    };

    fetchSections();

    return () => {
      isActive = false;
    };
  }, [mode]);

  return { sections, loading, states: STATES };
}